import {
  INSTITUTION_TAX_SELF_CERTIFICATION_SIGN_OFF_DEFAULTS,
  type InstitutionTaxSelfCertification,
  institutionTaxSelfCertificationDeclarationSchema,
} from '@/features/institution/tax-self-certification/schema';
import i18n from '@/lib/i18n';
import {
  type InstitutionDetailsV2Ownership,
  type InstitutionDetailsV2ProprietorTaxSelfCertification,
  type InstitutionDetailsV2ProprietorTaxSelfCertificationSubmission,
  institutionDetailsV2ProprietorTaxSelfCertificationSubmissionSchema,
  residenceKeysOf,
  soleProprietorOf,
} from '@/services/api/profile';
import { hasSameMembers } from '@/utils/refinements';

export type InstitutionProprietorTaxSelfCertification = Pick<InstitutionTaxSelfCertification, 'declaration' | 'signatory'>;

export type InstitutionProprietor = NonNullable<ReturnType<typeof soleProprietorOf>>;

export const institutionProprietorTaxSelfCertificationSchema = institutionTaxSelfCertificationDeclarationSchema;

export const getInstitutionProprietor = (ownership: InstitutionDetailsV2Ownership | undefined) => (ownership ? soleProprietorOf(ownership) : undefined);

// The proprietor certifies the residences their ownership record holds, so saving again picks up any change made there
export const institutionProprietorTaxSelfCertificationToInstitutionDetailsProprietorTaxSelfCertificationApi = (
  { declaration, signatory }: InstitutionProprietorTaxSelfCertification,
  proprietor: InstitutionProprietor,
): Promise<InstitutionDetailsV2ProprietorTaxSelfCertificationSubmission> =>
  institutionDetailsV2ProprietorTaxSelfCertificationSubmissionSchema.parseAsync({
    declaration,
    signatory: { ...signatory, partyId: proprietor.partyId, fullName: `${proprietor.firstName} ${proprietor.lastName}` },
    taxResidences: proprietor.taxResidences,
  });

export const isDeclaredForProprietorsResidences = (proprietorTaxSelfCertification: InstitutionDetailsV2ProprietorTaxSelfCertification, proprietor: InstitutionProprietor) =>
  hasSameMembers(residenceKeysOf(proprietorTaxSelfCertification.taxResidences), residenceKeysOf(proprietor.taxResidences));

// Each save dates a new self-certification, so it's declared afresh
export const createInstitutionProprietorTaxSelfCertificationDefaults = (
  proprietorTaxSelfCertification: InstitutionDetailsV2ProprietorTaxSelfCertification | undefined,
  proprietor: InstitutionProprietor | undefined,
): InstitutionProprietorTaxSelfCertification => {
  const isSameProprietor = proprietor !== undefined && proprietorTaxSelfCertification?.signatory.partyId === proprietor.partyId;

  return {
    declaration: INSTITUTION_TAX_SELF_CERTIFICATION_SIGN_OFF_DEFAULTS.declaration,
    signatory: {
      partyId: proprietor?.partyId,
      fullName: proprietor ? `${proprietor.firstName} ${proprietor.lastName}` : '',
      capacity: isSameProprietor ? proprietorTaxSelfCertification.signatory.capacity : i18n.t('institutionDetails.proprietorTaxSelfCertification.capacity'),
    },
  };
};
